import React, { useEffect, useState } from "react";
import { Outlet, NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, Instagram, Mail, Phone, ArrowUpRight } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import { BonkerLogo } from "@/components/BonkerLogo";
import { getSettings, getContentPages, igUrl, type SiteSettings, type ContentPage } from "@/lib/publicContent";

interface NavItem { to: string; label: string; end?: boolean; }
const NAV_BEFORE: NavItem[] = [
  { to: "/", label: "Home", end: true },
  { to: "/portfolio", label: "Portfolio" },
];
const NAV_AFTER: NavItem[] = [
  { to: "/faq", label: "FAQ" },
  { to: "/contatti", label: "Contatti" },
];
const PAGE_ROUTES: Record<string, string> = {
  cerimonia: "/scarpe-da-cerimonia",
  aziende: "/aziende",
};

export const PublicLayout: React.FC = () => {
  const [settings, setSettings] = useState<SiteSettings | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [pages, setPages] = useState<ContentPage[]>([]);
  const location = useLocation();

  useEffect(() => {
    getSettings().then(setSettings).catch(() => {});
    getContentPages().then(setPages).catch(() => {});
  }, []);

  const navItems: NavItem[] = [
    ...NAV_BEFORE,
    ...pages
      .filter((p) => PAGE_ROUTES[p.key])
      .map((p) => ({ to: PAGE_ROUTES[p.key], label: p.title || p.key })),
    ...NAV_AFTER,
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); window.scrollTo(0, 0); }, [location.pathname]);

  const logo = settings?.logo_url ? mediaUrl(settings.logo_url) : null;

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:z-[60] focus:top-3 focus:left-3 focus:bg-secondary focus:text-black focus:px-4 focus:py-2 focus:font-bold">
        Salta al contenuto
      </a>

      <header
        data-testid="public-header"
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
          scrolled || menuOpen ? "bg-background/95 backdrop-blur border-b border-white/10" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 md:px-10 h-16 md:h-20 flex items-center justify-between">
          <Link to="/" aria-label="Bonker home" data-testid="nav-logo">
            <BonkerLogo src={logo} className="h-8 md:h-10" />
          </Link>

          <nav aria-label="Navigazione principale" className="hidden md:flex items-center gap-9">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`nav-link-${n.to === "/" ? "home" : n.to.replace(/^\//, "")}`}
                className={({ isActive }) =>
                  `text-xs uppercase tracking-[0.18em] font-bold transition-colors ${
                    isActive ? "text-secondary" : "text-muted-foreground hover:text-foreground"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/configuratore"
              data-testid="nav-cta-configura"
              className="hidden sm:inline-flex items-center gap-2 bg-primary text-primary-foreground font-bold uppercase tracking-[0.12em] text-xs px-5 py-3 hover:-translate-y-[2px] transition-transform"
            >
              Configura <ArrowUpRight className="h-4 w-4" />
            </Link>
            <button
              className="md:hidden text-foreground p-2"
              aria-label={menuOpen ? "Chiudi menu" : "Apri menu"}
              aria-expanded={menuOpen}
              data-testid="mobile-menu-toggle"
              onClick={() => setMenuOpen((v) => !v)}
            >
              {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {menuOpen && (
          <nav aria-label="Navigazione mobile" className="md:hidden border-t border-white/10 bg-background px-5 py-6 flex flex-col gap-5" data-testid="mobile-menu">
            {navItems.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end}
                className={({ isActive }) => `text-lg font-display font-bold ${isActive ? "text-secondary" : "text-foreground"}`}>
                {n.label}
              </NavLink>
            ))}
            <Link to="/configuratore" className="mt-2 inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-bold uppercase tracking-[0.12em] text-sm px-5 py-4">
              Configura la tua <ArrowUpRight className="h-4 w-4" />
            </Link>
          </nav>
        )}
      </header>

      <main id="main" className="flex-1 pt-16 md:pt-20">
        <Outlet context={settings} />
      </main>

      <Footer settings={settings} nav={navItems} />
    </div>
  );
};

const LEGAL = [
  { to: "/privacy-policy", label: "Privacy Policy" },
  { to: "/cookie-policy", label: "Cookie Policy" },
  { to: "/termini", label: "Termini e Condizioni" },
];

const Footer: React.FC<{ settings: SiteSettings | null; nav: NavItem[] }> = ({ settings, nav }) => (
  <footer className="border-t border-white/10 bg-black relative bonker-noise" data-testid="public-footer">
    <div className="max-w-7xl mx-auto px-5 md:px-10 py-16 grid gap-12 md:grid-cols-[1.4fr_1fr_1fr_1fr] relative z-10">
      <div>
        <BonkerLogo src={settings?.logo_url ? mediaUrl(settings.logo_url) : null} className="h-9" />
        <p className="text-sm text-muted-foreground mt-5 max-w-xs leading-relaxed">
          Custom sneakers dipinte a mano. Trasformo le tue passioni in un paio unico.
        </p>
        {settings?.address && (
          <p className="text-xs text-muted-foreground/70 mt-4 whitespace-pre-line" data-testid="footer-address">{settings.address}</p>
        )}
      </div>

      <div>
        <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground/60 mb-4">Esplora</p>
        <ul className="space-y-3">
          {nav.map((n) => (
            <li key={n.to}><Link to={n.to} className="text-sm text-muted-foreground hover:text-secondary transition-colors">{n.label}</Link></li>
          ))}
          <li><Link to="/configuratore" className="text-sm text-primary font-semibold hover:underline">Configura la tua →</Link></li>
        </ul>
      </div>

      <div>
        <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground/60 mb-4">Contatti</p>
        <ul className="space-y-3" data-testid="footer-contacts">
          {settings?.contact_email && (
            <li><a href={`mailto:${settings.contact_email}`} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"><Mail className="h-4 w-4" /> {settings.contact_email}</a></li>
          )}
          {settings?.contact_phone && (
            <li><a href={`https://wa.me/${settings.contact_phone.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"><Phone className="h-4 w-4" /> {settings.contact_phone}</a></li>
          )}
          {igUrl(settings) && (
            <li><a href={igUrl(settings)!} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"><Instagram className="h-4 w-4" /> Instagram</a></li>
          )}
        </ul>
      </div>

      <div>
        <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground/60 mb-4">Legale</p>
        <ul className="space-y-3" data-testid="footer-legal">
          {LEGAL.map((l) => (
            <li key={l.to}><Link to={l.to} data-testid={`footer-legal-${l.to.replace("/", "")}`} className="text-sm text-muted-foreground hover:text-secondary transition-colors">{l.label}</Link></li>
          ))}
        </ul>
      </div>
    </div>
    <div className="border-t border-white/5">
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground/60" data-testid="footer-copyright">
          © {new Date().getFullYear()} {settings?.site_name ?? "Bonker"}. Tutti i diritti riservati.
          {settings?.vat_number && <span> · P.IVA {settings.vat_number}</span>}
        </p>
        <div className="flex items-center gap-4">
          <button
            type="button"
            data-testid="footer-cookie-prefs"
            onClick={() => window.dispatchEvent(new Event("open-cookie-preferences"))}
            className="text-xs text-muted-foreground/40 hover:text-muted-foreground transition-colors"
          >
            Preferenze cookie
          </button>
          <Link to="/cms/login" className="text-xs text-muted-foreground/40 hover:text-muted-foreground transition-colors">Area riservata</Link>
        </div>
      </div>
    </div>
  </footer>
);
